import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type LinkType = "category" | "product";

export interface ProductUpsellLink {
  id: string;
  product_id: string;
  link_type: LinkType;
  linked_category_id: string | null;
  linked_product_id: string | null;
  min_selections: number;
  max_selections: number;
  is_required: boolean;
  label: string | null;
  display_order: number;
  created_at: string;
  // Joined data
  linked_category?: {
    id: string;
    name: string;
  } | null;
  linked_product?: {
    id: string;
    name: string;
    price: number;
  } | null;
}

export interface CreateUpsellLinkData {
  product_id: string;
  link_type: LinkType;
  linked_category_id?: string;
  linked_product_id?: string;
  min_selections?: number;
  max_selections?: number;
  is_required?: boolean;
  label?: string;
  display_order?: number;
}

export interface UpdateUpsellLinkData {
  min_selections?: number;
  max_selections?: number;
  is_required?: boolean;
  label?: string;
  display_order?: number;
}

export function useProductUpsell(productId?: string) {
  const queryClient = useQueryClient();

  // Fetch all upsell links for a specific product
  const { data: upsellLinks = [], isLoading } = useQuery({
    queryKey: ["product-upsell-links", productId],
    queryFn: async () => {
      if (!productId) return [];

      const { data, error } = await supabase
        .from("product_upsell_links")
        .select(`
          *,
          linked_category:categories!linked_category_id (id, name),
          linked_product:products!linked_product_id (id, name, price)
        `)
        .eq("product_id", productId)
        .order("display_order");

      if (error) throw error;
      return data as unknown as ProductUpsellLink[];
    },
    enabled: !!productId,
  });

  // Fetch all products that have upsell links (for listing on the Adicionais page)
  const { data: productsWithUpsell = [], isLoading: isLoadingProducts, refetch: refetchProductsWithUpsell } = useQuery({
    queryKey: ["products-with-upsell"],
    queryFn: async () => {
      // Get all upsell links with product info
      const { data: links, error: linksError } = await supabase
        .from("product_upsell_links")
        .select(`
          *,
          product:products!product_id (id, name, price, category_id),
          linked_category:categories!linked_category_id (id, name),
          linked_product:products!linked_product_id (id, name, price)
        `)
        .order("display_order");

      if (linksError) throw linksError;

      // Group by product
      const productsMap = new Map<string, {
        id: string;
        name: string;
        price: number;
        category_id: string;
        upsellLinks: typeof links;
      }>();

      links?.forEach(link => {
        const product = (link as any).product;
        if (!product) return;

        if (!productsMap.has(product.id)) {
          productsMap.set(product.id, {
            ...product,
            upsellLinks: [],
          });
        }

        productsMap.get(product.id)!.upsellLinks.push(link);
      });

      return Array.from(productsMap.values());
    },
  });

  // Create upsell link
  const createMutation = useMutation({
    mutationFn: async (data: CreateUpsellLinkData) => {
      const { error } = await supabase
        .from("product_upsell_links")
        .insert(data);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-upsell-links"] });
      queryClient.invalidateQueries({ queryKey: ["products-with-upsell"] });
      toast.success("Vínculo de upsell criado com sucesso");
    },
    onError: (error) => {
      console.error("Erro ao criar vínculo:", error);
      toast.error("Erro ao criar vínculo de upsell");
    },
  });

  // Update upsell link
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUpsellLinkData }) => {
      const { error } = await supabase
        .from("product_upsell_links")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-upsell-links"] });
      queryClient.invalidateQueries({ queryKey: ["products-with-upsell"] });
      toast.success("Vínculo atualizado com sucesso");
    },
    onError: (error) => {
      console.error("Erro ao atualizar vínculo:", error);
      toast.error("Erro ao atualizar vínculo");
    },
  });

  // Delete upsell link
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("product_upsell_links")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-upsell-links"] });
      queryClient.invalidateQueries({ queryKey: ["products-with-upsell"] });
      toast.success("Vínculo removido com sucesso");
    },
    onError: (error) => {
      console.error("Erro ao remover vínculo:", error);
      toast.error("Erro ao remover vínculo");
    },
  });

  return {
    upsellLinks,
    isLoading,
    productsWithUpsell,
    isLoadingProducts,
    createLink: createMutation.mutateAsync,
    updateLink: updateMutation.mutateAsync,
    deleteLink: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
